import React from "react";
import styled from "styled-components";
import { FaInstagram, FaLinkedin, FaEnvelope, FaGithub } from "react-icons/fa"; // Importing icons
import Spline from "@splinetool/react-spline";
import TextShift from "../../utils/TextShift";
import RevealOnScroll from "../../utils/RevealOnScroll";
import ExplodingText from "../../utils/ExplodingText";

const AboutContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  width: 50%;
  padding-right: 2rem;

  @media (max-width: 1024px) {
    width: 100%;
    padding: 1rem;
    order: 1;
  }
`;

const Title = styled.h1`
  font-size: 2em;
  text-align: center;
  color: white;

  @media (max-width: 763px) {
    font-size: 1.5em; // Adjust font size for mobile
  }
`;

const Content = styled.div`
  font-size: 1.2rem;
  line-height: 1.6;
  margin: 2rem 0;
  max-width: 800px;
  text-align: justify;
  font-family: "maitree", sans-serif;
  color: #777;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 1rem;
    margin: 1rem 0;
  }
`;

const ParagraphContainer = styled.div`
  margin-bottom: 1rem;
  font-size: 1.2rem;
  line-height: 1.6;
  font-family: "maitree", sans-serif;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Letter = styled.span`
  color: #b2b2b2; // Letters start out as gray
  font-weight: 500;
  transition: color 7s ease; // Slow transition for fading back to gray
  &:hover {
    color: white; // Letters change to black on hover
    transition: color 0.01s ease; // Fast transition to black
  }

  @media (max-width: 763px) {
    font-size: 1em; // Adjust font size for mobile
  }
`;

const SocialLinksContainer = styled.div`
  display: flex;
  gap: 20px; // Maintain gap for a cleaner look
  flex-wrap: wrap; // This allows the buttons to wrap to the next line

  // You might want to adjust padding or margin here if needed
`;

const SocialLink = styled.a`
  display: inline-flex; // Change from flex to inline-flex for proper alignment
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 40px;
  padding: 10px 20px; // Adjust padding to fit content
  font-weight: 600;
  text-align: center;
  border: none;
  background-size: 300% 100%;
  border-radius: 50px;
  font-family: "maitree", sans-serif;
  transition: all 0.4s ease-in-out;
  background-image: linear-gradient(
    to right,
    rgba(114, 114, 114, 0),
    rgba(106, 106, 107, 0),
    rgba(98, 99, 101, 0),
    rgba(89, 92, 94, 0),
    rgba(80, 85, 87, 0),
    #505557,
    #4a4f51,
    #44494a,
    #3e4344,
    #393c3e,
    #353637,
    #303031,
    #2a2a2a
  );
  box-shadow: 0 4px 15px 0 rgb(242, 242, 242, 0.2);

  &:hover {
    background-position: 100% 0;
  }

  svg {
    transition: transform 0.2s ease; // Smooth transform for icons
  }

  &:hover svg {
    transform: scale(1.1); // Slightly enlarge icons on hover
  }
`;

const LayoutContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  padding: 8rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
  gap: 2rem;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: center;
    padding: 2rem 1rem;
    overflow-y: auto;
    height: 100%;
  }
`;

const SplineContainer = styled.div`
  flex: 1;
  height: 900px;
  width: 50%;
  
  @media (max-width: 1024px) {
    width: 100%;
    height: 400px;
    order: 3;
  }
`;

const TitleSection = styled.div`
  @media (max-width: 1024px) {
    order: 1;
    width: 100%;
    text-align: center;
    margin-bottom: -1rem;
  }
`;

const LinksSection = styled.div`
  @media (max-width: 1024px) {
    order: 2;
    width: 100%;
    display: flex;
    justify-content: center;
    margin: 2rem 0;
  }
`;

const ContentSection = styled.div`
  @media (max-width: 1024px) {
    order: 4;
    width: 100%;
  }
`;

// Utility function to wrap each letter in a Letter component
const wrapLetters = (text) => {
  return text
    .split("")
    .map((letter, index) => <Letter key={index}>{letter}</Letter>);
};

const About = () => {
  const paragraphs = [
    "I'm a experienced software developer skilled in blending artistic creativity with technical expertise. I excel in building full-stack applications, managing complex data systems, and developing AI-driven solutions. With a strong background in automation, specializing in creating workflows and pipelines that streamline processes, reduce inefficiencies, and enhance productivity. My work bridges the gap between innovative design and robust technical performance, delivering impactful solutions tailored to diverse needs.",
  ];

  return (
    <div>

      <LayoutContainer>
        <SplineContainer className="hoverable">
          <Spline scene="https://prod.spline.design/4dI6ztw3b63FUK0M/scene.splinecode" />
        </SplineContainer>
        <AboutContainer>
          <TitleSection>
            <RevealOnScroll>
              <Title>
                <TextShift
                  text="Hi, I'm Noah"
                  maxWeight={800}
                  minWeight={300}
                  fontSize="4rem"
                  mobileFontSize="3rem"
                  adjacentWeightChange={100}
                  fontFamily="moret-variable"
                />
              </Title>
            </RevealOnScroll>
          </TitleSection>
          <ContentSection>
            <RevealOnScroll delay={50}>
              <Content>
                {paragraphs.map((paragraph, index) => (
                  <ParagraphContainer key={index}>
                    <ExplodingText text={paragraph} />
                  </ParagraphContainer>
                ))}
              </Content>
            </RevealOnScroll>
          </ContentSection>
          <LinksSection>
            <RevealOnScroll delay={100}>
              <SocialLinksContainer>
                <SocialLink
                  href="https://www.instagram.com/noahkedits/"
                  target="_blank"
                >
                  <FaInstagram />
                </SocialLink>
                <SocialLink
                  href="https://www.linkedin.com/in/noah-kornberg-463149231/"
                  target="_blank"
                >
                  <FaLinkedin />
                </SocialLink>
                <SocialLink href="mailto:noahkornberg@gmail.com">
                  <FaEnvelope />
                </SocialLink>
                <SocialLink href="https://github.com/PinknMatter">
                  <FaGithub />
                </SocialLink>

                {/* Add more SocialLink components as needed */}
              </SocialLinksContainer>
            </RevealOnScroll>
          </LinksSection>
      
        </AboutContainer>
      </LayoutContainer>
    </div>
  );
};

export default About;
